import React from 'react';
import { Column } from 'react-table';
import { stringify } from 'query-string';
import { formatDate, getYearWithMonth } from '../../../../common/helpers/dateHelper';
import { RecordFilterType } from '../../../../common/enums/RecordFilterType';
import styles from './styles.module.scss';
import Highlighter from 'react-highlight-words';
import { FormattedMessage } from 'react-intl';
import { CandidateStatus } from '../../../../common/enums/CandidateStatus';
import { Search } from '../../../../common/models/search/Search';
import { Link } from 'react-router-dom';
import { ContactType } from '../../../../common/enums/ContactType';
import { ICandidateSearch } from '../../../../common/models/candidate/ICandidateSearch';
import { IPositionSearch } from '../../../../common/models/position/IPositionSearch';
import { ISurveySearch } from '../../../../common/models/survey/ISurveySearch';
import Icon from '../../../../components/Icon';
import IconButton from '../../../../components/IconButton';
import { IBindingAction, IBindingCallback1 } from '../../../../common/models/callback';
import LinkButton from '../../../../components/LinkButton';

const getTableIcon = (search: Search) => {
  switch (search.type) {
    case RecordFilterType.Candidate:
      return <Icon icon="candidates" className={styles.tableIcon} />;
    case RecordFilterType.Position:
      return <Icon icon="position" className={styles.tableIcon} />;
    default:
      return <Icon icon="survey" className={styles.tableIcon} />;
  }
};

const formatPositionName = (message: React.ReactNode, positionName: string | null) => {
  if (positionName) {
    return `${message} - ${positionName}`;
  }
  return (
    <>
      {message}
      {' - '}
      <FormattedMessage defaultMessage="No position" id="searchResult.noPosition" />
    </>
  );
};

const getCandidateDescription = (status: CandidateStatus, positionName: string | null) => (
  status === CandidateStatus.Active
    ? (
      <FormattedMessage id="settings.active" defaultMessage="Active">
        {mes => formatPositionName(mes, positionName)}
      </FormattedMessage>
    )
    : (
      <FormattedMessage id="settings.inactive" defaultMessage="Inactive">
        {mes => formatPositionName(mes, positionName)}
      </FormattedMessage>
    )
);

const getPositionDescription = (status: number, updatedAt: Date) => (
  status
    ? (
      <FormattedMessage id="settings.active" defaultMessage="Active" />
    )
    : (
      <FormattedMessage id="settings.inactive" defaultMessage="Inactive">
        {mes => `${mes} - ${getYearWithMonth(updatedAt)}`}
      </FormattedMessage>
    )
);

const getCellDescription = (search: Search) => {
  switch (search.type) {
    case RecordFilterType.Candidate:
      const { status, positionName } = search as ICandidateSearch;
      return (
        <span className={styles.searchResultLabel}>
          <span className={styles.searchResultDesc}>
            <FormattedMessage id="searchResult.candidate" defaultMessage="Candidate" />
          </span>
          {' - '}
          {getCandidateDescription(status, positionName)}
        </span>
      );
    case RecordFilterType.Position:
      const { status: st, updatedAt: updated } = search as IPositionSearch;
      return (
        <span className={styles.searchResultLabel}>
          <span className={styles.searchResultDesc}>
            <FormattedMessage id="searchResult.position" defaultMessage="Position" />
          </span>
          {' - '}
          {getPositionDescription(st, updated)}
        </span>
      );
    case RecordFilterType.Survey:
      return (
        <span className={`${styles.searchResultDesc} ${styles.searchResultLabel}`}>
          <FormattedMessage id="searchResult.survey" defaultMessage="Survey" />
        </span>
      );
    default:
      return undefined;
  }
};

export const getSearchString = (search: Search) => {
  switch (search.type) {
    case RecordFilterType.Candidate:
      const { id: candidateId, contactId } = search as ICandidateSearch;
      return stringify({ contactId, candidateId, role: ContactType.Candidate });
    case RecordFilterType.Position:
      const { id: positionId } = search as IPositionSearch;
      return stringify({ positionId });
    case RecordFilterType.Survey:
      const { id: surveyId } = search as ISurveySearch;
      return stringify({ surveyId, surveyBuilderShown: true });
    default:
      return '/';
  }
};

export const headerBlock = (
  searchCount: number,
  isRecent: boolean,
  clearFilters: IBindingAction,
  showClearFilters: boolean
) => (
  <div className={`d-flex ${styles.header}`}>
    {searchCount
      ? (
        <span className={`${styles.searchResultLabel} t-14 avenir-font`}>
          {
            isRecent
              ? <FormattedMessage id="searchInput.recent" defaultMessage="Recent pages" />
              : (
                <>
                  {searchCount}
                  {' '}
                  <FormattedMessage id="searchInput.results" defaultMessage="results" />
                </>
              )
          }
        </span>
      )
      : null}
    {showClearFilters
      ? (
        <div className="ml-auto">
          <LinkButton
            textIntlId="searchResult.clearFilters"
            defaultMessage="Clear filters"
            onClick={clearFilters}
          />
        </div>
      )
      : null}
  </div>
);

export const getColumns = (search: string, onRunReport: IBindingCallback1<string>): Column[] => [
  {
    accessor: (item: any) => (item.surveyDate ? formatDate(item.surveyDate) : ''),
    id: 'id',
    width: 32,
    Cell: ({ row: { original } }: any) => {
      const icon = getTableIcon(original);
      return (
        <div>
          {icon}
        </div>
      );
    }
  },
  {
    id: 'title',
    Cell: ({ row: { original } }) => {
      const { title } = original as Search;
      return (
        <div className="d-flex flex-column">
          <div className={styles.titleCell}>
            <Link
              to={{ pathname: window.location.pathname, search: getSearchString(original as Search) }}
              className={styles.title}
            >
              <Highlighter
                highlightClassName="highlighted-text"
                searchWords={[search]}
                textToHighlight={title || ''}
                autoEscape
              />
            </Link>
            <div className={styles.description}>{getCellDescription(original as Search)}</div>
          </div>
        </div>
      );
    }
  },
  {
    id: 'action',
    width: 30,
    Cell: ({ row: { original } }) => {
      const { id, type, runReport } = original as ICandidateSearch;
      return type === RecordFilterType.Candidate && runReport && (
        <div className={`${styles.iconButton} d-flex justify-content-center`}>
          <IconButton
            icon={<Icon icon="chart" className={styles.actionIcon} />}
            tooltip={{ id: 'runReport', defaultMessage: 'Run report' }}
            onClick={event => { onRunReport(id); event.stopPropagation(); }}
            variant="blue"
          />
        </div>
      );
    }
  }
];
